import * as THREE from 'three';

/** The group of cords and cradle that turns about the fist, and the stone in the cradle (`human-model.ts`). */
export const SLING_CORDS = 'sling-cords', SLING_STONE = 'sling-stone';
/** From the fist to the cradle, in rig metres. */
export const SLING_LENGTH = .6;
/**
 * The sling clip: the arm lifts the sling overhead and starts it turning at `start`, makes `turns` accelerating circles above the head, and lets
 * go at `release` (the clip's contact) as the cradle sweeps forward past the sling side. The stone is back in the cradle from `reload`.
 */
export const SLING_WHIRL = { start: .16, release: .62, turns: 3, reload: .94 } as const;
/** How far the whirl has turned (radians) at `progress`, slow at first and fastest at the release; zero before it starts. */
export function slingWhirlAngle(progress: number): number {
  const u = THREE.MathUtils.clamp((progress - SLING_WHIRL.start) / (SLING_WHIRL.release - SLING_WHIRL.start), 0, 1);
  return SLING_WHIRL.turns * Math.PI * 2 * u ** 1.7;
}
/** How much of the clip's weight is on the whirl: it rises as the arm lifts and falls away after the release. */
export function slingWhirlWeight(progress: number): number {
  return THREE.MathUtils.smoothstep(progress, SLING_WHIRL.start - .06, SLING_WHIRL.start + .06) * (1 - THREE.MathUtils.smoothstep(progress, SLING_WHIRL.release, SLING_WHIRL.release + .05));
}

const DOWN = new THREE.Vector3(0, -1, 0), CORD_AXIS = new THREE.Vector3(0, -1, 0);
const scratch = { root: new THREE.Quaternion(), parent: new THREE.Quaternion(), aim: new THREE.Quaternion(), circle: new THREE.Vector3(), pull: new THREE.Vector3() };

/**
 * The cords of a sling in hand, posed in world space: they hang from the fist and swing behind it as it moves, are whirled flat over the head
 * during the clip, and fly loose after the release before settling back. Presentation only.
 */
export class SlingCords {
  private direction = DOWN.clone();
  private velocity = new THREE.Vector3();

  pose(parts: Map<string, THREE.Object3D>, root: THREE.Object3D, progress: number | undefined, dt: number) {
    const sling = parts.get('sling'), cords = sling?.getObjectByName(SLING_CORDS);
    if (!sling || !cords) return;
    const step = Math.min(Math.max(dt, 0), 1 / 20);
    const weight = progress === undefined ? 0 : slingWhirlWeight(progress);
    if (weight > 0) {
      // The figure faces +Z with its sling hand toward -X. The cradle starts out on that side and turns so that it sweeps forward past it on
      // every turn; the circle is a little tilted, lower in front, so it passes the shoulder at head height.
      root.getWorldQuaternion(scratch.root);
      const angle = slingWhirlAngle(progress!);
      scratch.circle.set(-Math.cos(angle), -.18 - .12 * Math.sin(angle), Math.sin(angle)).normalize().applyQuaternion(scratch.root);
      const before = this.direction.clone();
      this.direction.lerp(scratch.circle, Math.min(1, weight * (.25 + 40 * step))).normalize();
      // Carry the whirl's speed into the release, so the cords fly on and wrap rather than drop dead.
      if (step > 0) this.velocity.copy(this.direction).sub(before).divideScalar(step);
    } else {
      // A pendulum under gravity, damped: the hand's own motion makes the cords trail and swing.
      scratch.pull.copy(DOWN).sub(this.direction).multiplyScalar(38);
      this.velocity.addScaledVector(scratch.pull, step).multiplyScalar(Math.exp(-3.2 * step));
      this.direction.addScaledVector(this.velocity, step).normalize();
      this.velocity.sub(scratch.pull.copy(this.direction).multiplyScalar(this.velocity.dot(this.direction)));
    }
    cords.parent!.getWorldQuaternion(scratch.parent);
    scratch.aim.setFromUnitVectors(CORD_AXIS, this.direction);
    cords.quaternion.copy(scratch.parent.invert().multiply(scratch.aim));
    const stone = cords.getObjectByName(SLING_STONE);
    if (stone) stone.visible = progress === undefined || progress < SLING_WHIRL.release || progress >= SLING_WHIRL.reload;
  }
  /** A fresh pose (a teleport, a rig swap): the cords simply hang. */
  reset() { this.direction.copy(DOWN); this.velocity.set(0, 0, 0); }
}
